import { useSyncExternalStore } from 'react';

/**
 * App auth store (#469).
 *
 * Holds the app-session user returned by `POST /api/auth/entra/callback`.
 * The session token itself lives in an httpOnly cookie set by the API — it is
 * never readable from JS. Only the non-sensitive user profile is kept here,
 * persisted to localStorage so the UI rehydrates across reloads AND native
 * app opens (the WKWebView starts a fresh sessionStorage per launch — with
 * session storage, every app open bounced to /login despite a valid 15-day
 * cookie); the cookie remains the real trust boundary, and a 401 from any
 * API call clears this store and bounces to /login.
 *
 * Dependency-free (no zustand in this repo): a tiny observable consumed by
 * React via useSyncExternalStore.
 */

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  roles: string[];
}

const STORAGE_KEY = 'ss-auth-user';

function load(): AuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

let user: AuthUser | null = load();
const listeners = new Set<() => void>();

function persist(): void {
  try {
    if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* localStorage unavailable — non-critical */ }
}

function emit(): void {
  listeners.forEach((listener) => listener());
}

export const authStore = {
  getUser(): AuthUser | null {
    return user;
  },
  isAuthenticated(): boolean {
    return user !== null;
  },
  setAuth(next: AuthUser): void {
    user = next;
    persist();
    emit();
  },
  clearAuth(): void {
    user = null;
    persist();
    emit();
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

/** React binding: re-renders the component when the session user changes. */
export function useAuthUser(): AuthUser | null {
  return useSyncExternalStore(authStore.subscribe, authStore.getUser);
}
